import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/LangContext";
import { ISLAND_BY_SLUG } from "../lib/constants";
import { getRecentReads } from "../lib/readerHistory";

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 60);

// Re-engagement nudge for returning readers. Shows up to 3 stories the
// visitor has opened in the last 7 days (excluding today's reads so it
// doesn't just duplicate what they just closed). Hidden when there's
// nothing to show — no scaffolding, no "nothing here" empty state.
export const ContinueReading = () => {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);

  useEffect(() => {
    const recent = getRecentReads({ maxAgeDays: 7, excludeToday: true, limit: 3 });
    if (recent.length === 0) {
      setItems([]);
      return;
    }
    let cancelled = false;
    const ids = recent.map((r) => r.id).join(",");
    api
      .get("/articles/by-ids", { params: { ids, lang } })
      .then((r) => {
        if (!cancelled) setItems(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  if (items.length === 0) return null;

  return (
    <section
      className="mb-8"
      data-testid="continue-reading"
    >
      <h2 className="text-[11px] uppercase tracking-[0.18em] text-[#0AADA0] mb-4">
        {t("continue_reading")}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((a, i) => {
          const meta = ISLAND_BY_SLUG[a.island];
          const to = `/article/${a.id}${a.slug ? `-${a.slug}` : `-${slugify(a.title)}`}?lang=${lang}`;
          return (
            <Link
              key={a.id}
              to={to}
              className="card-soft overflow-hidden flex group"
              data-testid={`continue-reading-item-${i}`}
            >
              {a.image_url ? (
                <img
                  src={a.image_url}
                  alt=""
                  className="w-24 h-24 object-cover flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-24 h-24 bg-[#F5F5F5] flex-shrink-0 flex items-center justify-center text-2xl">
                  {meta?.flag || "📰"}
                </div>
              )}
              <div className="min-w-0 flex-1 p-3 flex flex-col justify-between">
                <div className="text-[13px] text-[#1A2E35] leading-snug line-clamp-3 group-hover:text-[#0AADA0] transition-colors">
                  {a.title}
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1.5 flex items-center gap-1.5">
                  {meta && <span>{meta.flag}</span>}
                  {meta && <span>{meta.name}</span>}
                  <ArrowRight size={11} className="ml-auto text-[#0AADA0] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
